import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {RELATED_PRODUCTS_TAB_KEY_YOU_MAY_ALSO_LIKE} from 'constants/pdpRelatedProducts';
import useIntersectionObserverOnce from 'hooks/useIntersectionObserverOnce';
import ProductDetailsRow from 'product_details_page/ProductDetailsRow';
import ProductDetailsCard from 'product_details_page/RelatedProducts/RelatedProductCard';
import addColorToStylePath from 'utils/addColorToStylePath';
import {
  EVENT_TYPE,
  fireTabbedViewEvent,
  fireTabbedViewEventWithCallback
} from 'utils/metrics/pdpRelatedStylesTabbedView';
import {
  CarouselCardsView,
  EmptyStateCardsView,
  Loader,
  StyledRecoBox,
  Tabs
} from './components';

const CardsComponent = ({children, isLoading, length}) => {
  if (!isLoading && length === 0) {
    return <EmptyStateCardsView />;
  }
  if (isLoading) {
    return <Loader />;
  }
  if (length <= 4) {
    return <StyledRecoBox>{children}</StyledRecoBox>;
  }

  return <CarouselCardsView>{children}</CarouselCardsView>;
};

CardsComponent.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool.isRequired,
  length: PropTypes.number.isRequired
};

const RelatedProductsTabbedView = ({
  activeTab = RELATED_PRODUCTS_TAB_KEY_YOU_MAY_ALSO_LIKE,
  hasAnyData = true,
  isLoading,
  onTabChange,
  products = [],
  relatedProductsRef,
  tabs = []
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!hasAnyData && !isLoading) {
      setIsVisible(false);
    } else if (hasAnyData || isLoading) {
      setIsVisible(true);
    }
  }, [hasAnyData, isLoading]);
  useIntersectionObserverOnce(relatedProductsRef, () => {
    fireTabbedViewEvent({
      eventType: EVENT_TYPE.VIEWED
    });
  });
  const handleCardClick = (styleId, href) => {
    fireTabbedViewEventWithCallback({
      callback: () => {
        window.location.href = href;
      },
      eventData: {
        activeTab,
        eventType: EVENT_TYPE.ITEM_CLICKED,
        styleId
      }
    });
  };
  const slides = useMemo(
    () =>
      products.map((product) => (
        <ProductDetailsCard
          sx={{
            width: '100%'
          }}
          key={`related-styles-${activeTab.toLowerCase()}-${product.name}`}
          onClick={() =>
            handleCardClick(
              product.style_id,
              addColorToStylePath(product.link, product.color)
            )
          }
          imageSrc={product.image_src}
          imageSrcSet={product.image_srcset}
          minQty={product.min_qty}
          name={product.name}
          ratingCount={product.rating_count}
          ratingScore={product.rating_score}
          sizing={product.sizing}
          sizes={product.sizes}
          generalComments={product.general_comments}
          defaultUnitPrices={product.default_unit_prices}
          primaryCategoryId={product.primary_category_id}
          styleType={product.style_type}
          defaultQuoteQty={product.default_quote_qty}
          productId={product.style_id}
        />
      )),
    [activeTab, products]
  );

  return (
    <div
      style={{
        maxHeight: isVisible ? '62.5rem' : '0',
        opacity: isVisible ? 1 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.5s ease-out, opacity 0.5s ease-out'
      }}>
      <ProductDetailsRow
        childrenRef={relatedProductsRef}
        title="Recommended for you">
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
        <CardsComponent
          key={activeTab}
          isLoading={isLoading}
          length={products.length}>
          {slides}
        </CardsComponent>
      </ProductDetailsRow>
    </div>
  );
};

RelatedProductsTabbedView.propTypes = {
  activeTab: PropTypes.string,
  hasAnyData: PropTypes.bool,
  isLoading: PropTypes.bool,
  onTabChange: PropTypes.func,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      general_comments: PropTypes.string,
      image_src: PropTypes.string,
      image_srcset: PropTypes.string,
      link: PropTypes.string,
      min_qty: PropTypes.number,
      name: PropTypes.string,
      rating_count: PropTypes.number,
      rating_score: PropTypes.number,
      sizing: PropTypes.string,
      style_id: PropTypes.string
    })
  ),
  relatedProductsRef: PropTypes.object,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  )
};

export default RelatedProductsTabbedView;
